import { Box, Grid } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';
import { measureElement as measureRenderedElement, useVirtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useElementWidth } from '@/hooks/use-element-width';
import { useScrollMargin } from '@/hooks/use-scroll-margin';
import { useScrollParent } from '@/hooks/use-scroll-parent';
import { chunk } from '@/lib/chunk';
import MentionCard from './MentionCard';
import MentionCardSkeleton from './MentionCardSkeleton';
import { columnsForWidth, estimateRowHeight, MENTIONS_GRID_GAP_PX } from './mentions-grid-layout';

const NEXT_PAGE_SKELETON_COUNT = 5;
/** Rows kept mounted beyond each edge of the viewport, so media loads before it scrolls in. */
const OVERSCAN_ROWS = 2;
/** The next page is requested once the last mounted row is this close to the end. */
const LOAD_AHEAD_ROWS = 2;

type GridItem = { kind: 'mention'; mention: Mention } | { kind: 'skeleton'; key: string };

interface MentionsVirtualGridProps {
  mentions: Mention[];
  showCreator: boolean;
  compact: boolean;
  mediaAspectRatio: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

/**
 * The mentions grid, virtualized by row: only the rows near the viewport are
 * mounted, whatever the number of mentions loaded. Each card carries a video
 * element or a full-size image, a media request and several layers; keeping
 * every card of a long scroll mounted exhausted GPU memory and the browser
 * painted blank tiles. The list scrolls inside its nearest scrollable ancestor
 * (the tab panel, the contact drawer), not the window.
 */
export const MentionsVirtualGrid: React.FC<MentionsVirtualGridProps> = ({
  mentions,
  showCreator,
  compact,
  mediaAspectRatio,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}) => {
  const { sentinelRef: containerRef, scrollParent } = useScrollParent({ requireOverflow: false });
  const width = useElementWidth(containerRef);
  const columns = columnsForWidth(width, compact);
  const gap = MENTIONS_GRID_GAP_PX[compact ? 'compact' : 'regular'];

  const rows = useMemo(() => {
    const items: GridItem[] = mentions.map((mention) => ({ kind: 'mention', mention }));
    if (isFetchingNextPage) {
      for (let index = 0; index < NEXT_PAGE_SKELETON_COUNT; index += 1) {
        items.push({ kind: 'skeleton', key: `next-page-skeleton-${index}` });
      }
    }
    return chunk(items, columns);
  }, [mentions, isFetchingNextPage, columns]);

  const scrollMargin = useScrollMargin(containerRef, scrollParent, [width, mentions.length]);

  const estimateSize = useCallback(
    () => estimateRowHeight({ width, columns, compact, mediaAspectRatio }),
    [width, columns, compact, mediaAspectRatio]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollParent,
    estimateSize,
    // A row that measures 0 is not laid out (a hidden tab, jsdom): trusting it
    // would collapse the list and mount every card at once.
    measureElement: (element, entry, instance) => {
      const size = measureRenderedElement(element, entry, instance);
      return size > 0 ? size : estimateSize();
    },
    overscan: OVERSCAN_ROWS,
    gap: gap.row,
    scrollMargin,
    initialRect: { width: 0, height: window.innerHeight },
  });

  // Rows are cut again when the column count changes: heights measured for the
  // previous cut no longer describe the rows at those indexes.
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer, columns]);

  const virtualRows = virtualizer.getVirtualItems();
  const lastMountedRow = virtualRows[virtualRows.length - 1]?.index ?? -1;

  // Mirrored so a fresh callback identity each render does not re-run the effect.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  // The list and the view at the last request. A page that failed settles with
  // the same list, so asking again from the same spot would loop against a
  // failing backend; a new request waits for a new list (a page arrived, the
  // filters changed) or for the view to move.
  const lastRequestRef = useRef<{ mentions: Mention[]; row: number } | null>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || lastMountedRow < 0) return;
    if (lastMountedRow < rows.length - 1 - LOAD_AHEAD_ROWS) return;
    const last = lastRequestRef.current;
    if (last?.mentions === mentions && last.row === lastMountedRow) return;
    lastRequestRef.current = { mentions, row: lastMountedRow };
    onLoadMoreRef.current?.();
  }, [lastMountedRow, rows.length, mentions, hasNextPage, isFetchingNextPage]);

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="full"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualRows.map((virtualRow) => (
        <Grid
          key={virtualRow.key}
          ref={virtualizer.measureElement}
          data-index={virtualRow.index}
          position="absolute"
          top={0}
          left={0}
          w="full"
          templateColumns={`repeat(${columns}, minmax(0, 1fr))`}
          columnGap={`${gap.column}px`}
          // Inline, not a style prop: every offset would otherwise mint a CSS class.
          style={{ transform: `translateY(${virtualRow.start - scrollMargin}px)` }}
        >
          {(rows[virtualRow.index] ?? []).map((item) =>
            item.kind === 'mention' ? (
              <MentionCard
                key={item.mention.id}
                mention={item.mention}
                showCreator={showCreator}
                compact={compact}
                mediaAspectRatio={mediaAspectRatio}
              />
            ) : (
              <MentionCardSkeleton
                key={item.key}
                compact={compact}
                showCreator={showCreator}
                mediaAspectRatio={mediaAspectRatio}
              />
            )
          )}
        </Grid>
      ))}
    </Box>
  );
};

export default MentionsVirtualGrid;
